import React, { useEffect, useState, useCallback } from "react";
import { ConnectEmbed, useActiveAccount, useDisconnect } from "thirdweb/react";
import { client, wallets } from "../../constants/client";
import { createAuth, signLoginPayload } from "thirdweb/auth";
import { getUserEmail } from "thirdweb/wallets";
import Typography from "../../components/Typography/Typography";
import "./auth.scss";
import { useLoginMutation } from "../../services/authService";
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME } from "../../constants";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { authActions } from "../../services/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { logEvent, updateAmplitudeUserId } from "../../utils/amplitudeUtlis";
import Loading from "../../components/LoadingSkeleton/Loading";
import { useEncryption } from "../../hooks/encryptionHooks";
import { NETWORKS } from "../../web3/networks";

export default function CommonAuthComponent({ variant = "signin" }) {
    const [isLoading, setIsLoading] = useState(false);
    const [signIn] = useLoginMutation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { encrypt } = useEncryption();
    const { disconnect } = useDisconnect();
    const activeAccount = useActiveAccount();

    const chainId = process.env.REACT_APP_DEFAULT_CHAIN_ID || "11155111";
    const activeChainData = NETWORKS[chainId];

    // Get invitation from URL
    const queryParams = new URLSearchParams(location.search);
    const invitation = queryParams.get("invitation");

    // Unified authentication function that can be used by both flows
    const authenticateUser = useCallback(
        async (payload, signature, email, invitationToken = "") => {
            setIsLoading(true);

            try {
                // Log event for analytics
                logEvent({
                    eventName: invitationToken
                        ? "user_login_initiated"
                        : "tenant_login_initiated",
                    eventProperties: "",
                });

                updateAmplitudeUserId(email);

                // Encrypt payload
                const payloadString =
                    typeof payload === "string"
                        ? payload
                        : JSON.stringify(payload);
                const encryptedPayload = await encrypt(payloadString);

                // Call API with abort signal
                const signInRes = await signIn({
                    payload: encryptedPayload,
                    signature: signature,
                    email: email,
                    invitationToken,
                });

                if (!signInRes.data) {
                    const errorMessage =
                        signInRes?.error?.data?.errors?.[0] || "Login failed";
                    toast.error(errorMessage);

                    logEvent({
                        eventName: invitationToken
                            ? "user_login_failed"
                            : "tenant_login_failed",
                        eventProperties: { status: "Failed" },
                    });

                    return { success: false, error: errorMessage };
                }

                const { tokens, user } = signInRes?.data?.data;

                // Use a more secure approach - consider moving to HTTP-only cookies in production
                // For now we'll still use localStorage but note the security risk
                if (tokens?.access)
                    localStorage.setItem(ACCESS_TOKEN_NAME, tokens.access);
                if (tokens?.refresh)
                    localStorage.setItem(REFRESH_TOKEN_NAME, tokens.refresh);

                dispatch(authActions.login(user));

                logEvent({
                    eventName: invitationToken
                        ? "user_login_completed"
                        : "tenant_login_completed",
                    eventProperties: {
                        email: email,
                        role: user?.role,
                        status: "Success",
                    },
                });

                // Handle navigation based on user state
                if (user?.id === null) {
                    navigate("/signin");
                } else if (
                    user?.tenant === null ||
                    user?.reg_status === "CREATED" || user?.reg_status === "INVITED"
                ) {
                    navigate("/onboarding");
                } else {
                    navigate("/overview");
                }

                return { success: true, user };
            } catch (error) {
                // Don't handle AbortError as an actual error
                if (error.name === "AbortError")
                    return { success: false, aborted: true };

                console.error("Error during authentication:", error);
                toast.error(
                    error.message || "Authentication failed. Please try again.",
                );

                logEvent({
                    eventName: invitationToken
                        ? "user_login_failed"
                        : "tenant_login_failed",
                    eventProperties: {
                        status: "Error",
                        error: error.message,
                    },
                });

                return {
                    success: false,
                    error: error.message || "Authentication failed",
                };
            } finally {
                setIsLoading(false);
            }
        },
        [signIn, encrypt, dispatch, navigate],
    );

    // Direct login function using the unified authenticateUser
    const login = useCallback(async () => {
        try {
            setIsLoading(true); // Add this line to start loading before signature

            const auth = createAuth({
                domain: "hyperglade.local",
                client: client,
                login: {
                    resources: ["https://hyperglade.io"],
                    payloadExpirationTimeSeconds: 86400,
                },
            });

            const loginPayload = await auth.generatePayload({
                address: activeAccount?.address,
                chainId: activeChainData?.chainId,
            });

            const signedPayload = await signLoginPayload({
                payload: loginPayload,
                account: activeAccount,
            });

            const email = await getUserEmail({ client });

            return authenticateUser(
                signedPayload?.payload,
                signedPayload?.signature,
                email,
                invitation || "",
            );
        } catch (error) {
            console.error("Error preparing authentication:", error);
            toast.error("Failed to prepare authentication. Please try again.");
            setIsLoading(false); // Make sure to reset loading on error
            return { success: false, error: error.message };
        }
    }, [activeAccount, authenticateUser, invitation]);

    // Auto-login option (currently disabled)
    useEffect(() => {
        if (activeAccount?.address) {
            login();
        }
    }, [activeAccount?.address, login]);

    const authUser = useSelector((state) => state.auth.user);

    useEffect(() => {
        if (!authUser) return;

        if (authUser?.id === null) {
            navigate("/signin");
        } else if (
            authUser?.tenant === null ||
            authUser?.reg_status?.trim()?.toUpperCase() !== "COMPLETED" 
        ) {
            navigate("/onboarding");
        } else {
            navigate("/overview");
        }
    }, [authUser, navigate]);

    return (
        <div className="container-fluid signin-container vh-100 w-100 m-0 p-0 d-flex align-items-center justify-content-center">
            <div className="col-8 col-md-4 d-flex flex-column justify-content-around align-items-center">
                <div className="logo text-center mb-3">
                    <img
                        src="/images/hyperglade-logo-black.svg"
                        alt="logo"
                        width="200px"
                    />
                </div>

                <div className="text-center mb-5">
                    {variant === "signin" && (
                        <Typography variant="display1">
                            Sign in to your business account
                        </Typography>
                    )}
                    {variant === "signup" && (
                        <Typography variant="display1">
                            Create an account to get started with Hyperglade
                        </Typography>
                    )}
                </div>
                <div className="text-center d-flex align-items-center justify-content-center w-100 mb-4">
                    <ConnectEmbed
                        client={client}
                        wallets={wallets}
                        size="compact"
                        header={{ title: "" }}
                        auth={{
                            getLoginPayload: async (params) => {
                                setIsLoading(true); // Start loading before signature request
                                try {
                                    // Create auth object and generate payload
                                    const auth = createAuth({
                                        domain: "hyperglade.local",
                                        client: client,
                                        login: {
                                            resources: [
                                                "https://hyperglade.io",
                                            ],
                                            payloadExpirationTimeSeconds: 86400,
                                        },
                                    });

                                    return auth.generatePayload({
                                        address: params.address,
                                        chainId:
                                            params.chainId ||
                                            activeChainData?.chainId,
                                    });
                                } catch (error) {
                                    setIsLoading(false); // Reset on error
                                    throw error;
                                }
                            },
                            doLogin: async (params) => {
                                try {
                                    const email = await getUserEmail({
                                        client,
                                    });
                                    return authenticateUser(
                                        params.payload,
                                        params.signature,
                                        email,
                                        invitation || "",
                                    );
                                } catch (error) {
                                    console.error("Login error:", error);
                                    return {
                                        success: false,
                                        error:
                                            error.message ||
                                            "Authentication failed",
                                    };
                                }
                            },
                            isLoggedIn: async () => {
                                const accessToken =
                                    localStorage.getItem(ACCESS_TOKEN_NAME);
                                return { isLoggedIn: !!accessToken };
                            },
                            doLogout: async () => {
                                await disconnect();
                                localStorage.removeItem(ACCESS_TOKEN_NAME);
                                localStorage.removeItem(REFRESH_TOKEN_NAME);
                                dispatch(authActions.logout());
                                return { success: true };
                            },
                        }}
                    />
                </div>

                {isLoading && (
                    <div className="d-flex justify-content-center align-items-center">
                        <Loading />
                    </div>
                )}

                <footer className="fixed-bottom p-3 text-center">
                    {variant === "signin" && (
                        <Typography variant="subtitle">
                            New to Hyperglade?{" "}
                            <Link to="/register" className="register-text">
                                Register now
                            </Link>
                        </Typography>
                    )}
                    {variant === "signup" && (
                        <Typography variant="subtitle">
                            Already have an account?{" "}
                            <Link to="/signin" className="register-text">
                                Sign in
                            </Link>
                        </Typography>
                    )}
                </footer>
            </div>
        </div>
    );
}
